import React, { useEffect, useState } from 'react';
import { useFeedback } from '../../context/FeedbackContext';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * Auto-prompt that appears 150 s after the welcome splash closes (the
 * timer lives in `FeedbackContext`). Two layouts share one component
 * because the copy + dismissal behavior are identical:
 *
 *   - Desktop (≥ md): bottom-right slide-in card, ~360 px wide. No
 *     backdrop — the user can keep exploring underneath. Soft entrance
 *     animation (translateY + fade).
 *
 *   - Mobile (< md): centered modal with a backdrop. The mobile bottom-
 *     sheet already owns the bottom of the screen, so a corner toast would
 *     fight it; a centered prompt is unambiguous.
 *
 * "Share feedback" on either layout opens the Google Form in a new tab
 * (Forms iframes are blocked when the form has any sign-in restriction
 * and render badly on phones). The original dashboard tab keeps the
 * user's exploration state for when they return.
 *
 * "Maybe later" / × dismiss writes localStorage so the prompt doesn't
 * re-fire on refresh or navigation. Pressing "Share feedback" doesn't
 * write the dismissed flag — if the user opens the form but bails
 * without submitting, the persistent pill remains available on next
 * visit.
 */
export const FeedbackPrompt: React.FC = () => {
  const { promptVisible, openSurvey, dismissPrompt } = useFeedback();
  const isMobile = useIsMobile();

  // One-frame mount delay so the entrance transition runs (Tailwind's
  // `transition-*` classes can't animate from `display: none`). On exit,
  // we just unmount — a fade-out would feel laggy when the user has
  // already chosen "Maybe later".
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!promptVisible) {
      setEntered(false);
      return;
    }
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, [promptVisible]);

  // Esc closes on either layout — matches the modal-dismiss convention
  // already used elsewhere in the dashboard (AboutModal, WelcomeModal).
  useEffect(() => {
    if (!promptVisible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissPrompt();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [promptVisible, dismissPrompt]);

  if (!promptVisible) return null;

  const handleShare = () => {
    openSurvey();
  };

  // Audience-agnostic copy — the dashboard launches at the SFI annual
  // conference but is also reachable as a public link, so the prompt
  // can't assume the visitor is in the conference room. Keep it
  // friendly and short; provenance/use-case live in the form itself.
  const headline = 'How was your visit?';
  const body =
    'Quick feedback shapes where this dashboard goes next. Under 2 minutes.';

  if (isMobile) {
    return (
      <div
        className="md:hidden fixed inset-0 z-[1900] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
        // env(safe-area-inset-*) keeps the modal from sitting under the
        // iOS notch / status bar / home-indicator area. Without this the
        // flex centering uses the layout viewport (which extends under
        // unsafe areas), so the dialog reads as visually offset on
        // notched devices.
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Share feedback"
        onClick={dismissPrompt}
      >
        <div
          className={[
            'relative w-full max-w-sm bg-figma-panel rounded-figma-panel shadow-figma-card border border-figma-mint/40 overflow-hidden',
            'transition-all duration-300 ease-out',
            entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
          ].join(' ')}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Brand chrome strip — matches WelcomeModal so the prompt reads as
              part of the same family of dialogs */}
          <div
            className="h-1.5 bg-gradient-to-r from-figma-lime via-figma-lime to-figma-mint"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={dismissPrompt}
            aria-label="Dismiss feedback prompt"
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-figma-ink-soft hover:text-figma-text hover:bg-figma-card transition-smooth"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M1 1l10 10M11 1L1 11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div className="p-5 pt-6">
            <span className="text-[10px] font-roboto-mono font-bold uppercase tracking-[0.16em] text-figma-mint">
              We&rsquo;d love your input
            </span>
            <h2 className="mt-2 font-anton font-normal text-[26px] leading-[1.05] tracking-[0.005em] uppercase text-figma-text m-0">
              {headline}
            </h2>
            <p className="mt-3 font-roboto text-[14px] leading-relaxed text-figma-ink-soft">
              {body}
            </p>
            <p className="mt-2 text-[11px] font-roboto text-figma-ink-mute">
              Opens in a new tab.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleShare}
                className="w-full inline-flex items-center justify-center gap-2 px-4 h-11 rounded-figma-button bg-figma-mint text-figma-panel font-roboto font-bold text-sm uppercase tracking-wider hover:bg-figma-mint/90 transition-smooth"
              >
                <span>Share feedback</span>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path
                    d="M5 9l4-4M5 5h4v4"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={dismissPrompt}
                className="w-full px-4 h-11 rounded-figma-button bg-transparent border border-figma-ink-mute/40 text-figma-ink-soft font-roboto text-sm hover:text-figma-text hover:border-figma-ink-mute transition-smooth"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop: bottom-right slide-in card. No backdrop — the user can keep
  // exploring the dashboard underneath while the prompt sits in the corner.
  return (
    <div
      className={[
        'hidden md:block fixed bottom-6 right-6 z-[1500] w-[360px] bg-figma-panel rounded-figma-panel shadow-figma-card border border-figma-mint/40 overflow-hidden',
        'transition-all duration-300 ease-out',
        entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
      ].join(' ')}
      role="dialog"
      aria-label="Share feedback"
    >
      <div
        className="h-1.5 bg-gradient-to-r from-figma-lime via-figma-lime to-figma-mint"
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={dismissPrompt}
        aria-label="Dismiss feedback prompt"
        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full text-figma-ink-soft hover:text-figma-text hover:bg-figma-card transition-smooth"
      >
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
          <path
            d="M1 1l10 10M11 1L1 11"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <div className="p-5">
        <span className="text-[10px] font-roboto-mono font-bold uppercase tracking-[0.16em] text-figma-mint">
          We&rsquo;d love your input
        </span>
        <h3 className="mt-1.5 font-anton font-normal text-[22px] leading-[1.1] tracking-[0.005em] uppercase text-figma-text m-0">
          {headline}
        </h3>
        <p className="mt-2 font-roboto text-[13px] leading-relaxed text-figma-ink-soft">
          {body}
        </p>
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={handleShare}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 h-10 rounded-figma-button bg-figma-mint text-figma-panel font-roboto font-bold text-[12px] uppercase tracking-wider hover:bg-figma-mint/90 transition-smooth"
          >
            <span>Share feedback</span>
          </button>
          <button
            type="button"
            onClick={dismissPrompt}
            className="px-3 h-10 rounded-figma-button bg-transparent text-figma-ink-soft font-roboto text-[12px] hover:text-figma-text transition-smooth"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};
